import { useEffect, useRef, useState } from 'react'

import { Aviso } from '../componentes/ui'
import { CampoGenerico } from '../motor/Campo'
import type { GrupoDelEsquema } from '../motor/esquema'
import { useGruposQueAplican } from '../motor/esquema'
import { useFormulario } from '../motor/Formulario'

/**
 * Una pestaña por grupo de campos (plan del CMS dinámico §5.3): los campos a medida que el
 * núcleo dice que aplican a este documento, pintados con los mismos controles que todo lo
 * demás. Los valores viven en `datos.<clave>`, y el núcleo los valida al guardar contra los
 * grupos que apliquen **entonces**.
 *
 * Qué grupos aplican depende de lo que lleve escrito el documento (la categoría, la
 * plantilla): la lista de pestañas sale del esquema con el documento guardado, y aquí se le
 * vuelve a preguntar al núcleo con lo que hay en el editor. Si este grupo ya no aplica con lo
 * elegido, se avisa: sus valores se vaciarán al guardar. Un grupo que **empiece** a aplicar
 * aparece como pestaña después de guardar.
 */
const COLUMNAS: Record<string, string> = { completo: 'sm:col-span-6', mitad: 'sm:col-span-3', tercio: 'sm:col-span-2', 'dos-tercios': 'sm:col-span-4' }

/** Lo que una regla de ubicación puede mirar: solo con esto se vuelve a preguntar. */
const LO_QUE_SE_MIRA = ['categorias', 'plantilla', 'terminos', 'tipo'] as const

export const PestanaDeGrupo = ({ coleccion, grupo, id }: { coleccion: string; grupo: GrupoDelEsquema; id: null | number }) => {
  const { valores, sitio } = useFormulario()
  const { mutateAsync } = useGruposQueAplican(coleccion)
  const [aplica, setAplica] = useState(true)
  const firma = JSON.stringify(LO_QUE_SE_MIRA.map((c) => valores[c] ?? null))
  const anterior = useRef<null | string>(null)

  useEffect(() => {
    // La primera vez el esquema ya lo dijo; solo se pregunta cuando cambia algo que las reglas miran.
    if (anterior.current === null) {
      anterior.current = firma
      return
    }
    if (anterior.current === firma) return
    anterior.current = firma
    const documento = Object.fromEntries(LO_QUE_SE_MIRA.filter((c) => valores[c] !== undefined).map((c) => [c, valores[c]]))
    let vigente = true
    mutateAsync({ sitio, id, documento })
      .then((r) => {
        if (vigente) setAplica(r.grupos.some((g) => g.id === grupo.id))
      })
      .catch(() => {
        /* si el núcleo no contesta, se deja como estaba: lo decide al guardar */
      })
    return () => {
      vigente = false
    }
  }, [firma, grupo.id, id, mutateAsync, sitio, valores])

  return (
    <div className="flex flex-col gap-4">
      {!aplica && (
        <Aviso tono="aviso">
          Con lo que has elegido (categoría, plantilla…) este grupo ya no aparece en este documento: lo que escribas aquí se vaciará al guardar. Si lo quieres conservar, vuelve a la elección de antes.
        </Aviso>
      )}
      {grupo.nombre !== grupo.pestana && <p className="text-xs text-tenue">{grupo.nombre}</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
        {grupo.campos.map((campo) => (
          <div className={COLUMNAS[campo.ancho ?? 'completo'] ?? 'sm:col-span-6'} key={campo.nombre}>
            <CampoGenerico campo={campo} ruta={`datos.${campo.nombre}`} />
          </div>
        ))}
      </div>
    </div>
  )
}
